import { useState, useCallback, useRef, useEffect } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { api, ApiError, buildQueryString } from "@/lib/api";
import { Loader2, Film, Trash2, X, Download, Lock, Hourglass } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { createPortal } from "react-dom";
import { useUserStore } from "@/stores/user-store";
import { RETENTION_DAYS, hasExpiredFiles } from "@/config/retention";
import type { Video } from "@/types";

const PAGE_SIZE = 20;

function useDownload() {
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(async (url: string, filename: string) => {
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank");
    } finally {
      setDownloading(false);
    }
  }, []);

  return { download, downloading };
}

function getFilename(video: Video) {
  const ext = video.media_type === "IMAGE" ? "png" : "mp4";
  const slug = video.prompt.slice(0, 40).replace(/[^a-zA-Z0-9]+/g, "-").replace(/-+$/, "");
  return `animepup-${slug}.${ext}`;
}

export function AssetsPage() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { hasPurchased } = useUserStore();
  const [selected, setSelected] = useState<Video | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/videos/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["videos"] });
      setDeleteTarget(null);
    },
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["videos", user?.id],
    queryFn: async ({ pageParam = 0 }) => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: Video[] }>(
        `/videos${buildQueryString({ user_id: user.id, limit: String(PAGE_SIZE), offset: String(pageParam) })}`,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if ((lastPage.videos?.length ?? 0) < PAGE_SIZE) return undefined;
      return allPages.reduce((sum, p) => sum + (p.videos?.length ?? 0), 0);
    },
    enabled: !!user?.id,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages;
      if (!pages) return false;
      const allVids = pages.flatMap((p) => p.videos ?? []);
      const hasPending = allVids.some((v) => v.status === "pending" || v.status === "processing");
      return hasPending ? 5000 : false;
    },
  });

  const videos = data?.pages.flatMap((p) => p.videos ?? []) ?? [];
  // Expired runs keep their tile, but their files are gone: leave them out of counts and downloads.
  const liveVideos = videos.filter((v) => !hasExpiredFiles(v));

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto">
      <div className="mb-8 animate-fade-in-up flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight mb-1">Assets</h1>
          <p className="text-sm text-muted-foreground">
            {liveVideos.length > 0
              ? `${liveVideos.length} character${liveVideos.length === 1 ? "" : "s"} & scene${liveVideos.length === 1 ? "" : "s"}`
              : "Your characters and animations will appear here"}
          </p>
        </div>
        {liveVideos.some((v) => v.download_url) && <DownloadAllButton />}
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {Array.from({ length: 8 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {!isLoading && videos.length === 0 && (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="size-16 rounded-3xl bg-white/5 border border-white/8 flex items-center justify-center mb-4">
            <Film className="size-7 text-[#d4a574]" strokeWidth={1.5} />
          </div>
          <h3 className="text-base font-bold text-foreground mb-1.5">No characters yet</h3>
          <p className="text-sm text-muted-foreground mb-6">Create your first anime character to get started</p>
          <Link to="/create-character" className="btn-primary text-sm">Create Character</Link>
        </div>
      )}

      {liveVideos.length > 0 && !hasPurchased && (
        <Link
          to="/billing"
          className="flex items-center gap-3 p-4 rounded-2xl card-glass border border-[#d4a574]/20 mb-4 group hover:border-[#d4a574]/40 transition-all animate-fade-in"
        >
          <div className="size-9 rounded-xl bg-[#d4a574]/10 border border-[#d4a574]/20 flex items-center justify-center shrink-0">
            <Lock className="size-4 text-[#d4a574]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-foreground">Your creations are watermarked</p>
            <p className="text-[11px] text-muted-foreground">Purchase any gizmo pack to remove watermarks and download clean files</p>
          </div>
          <span className="text-[11px] font-bold text-[#d4a574] group-hover:translate-x-0.5 transition-transform shrink-0">Remove Watermarks →</span>
        </Link>
      )}

      {videos.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 animate-fade-in delay-100">
            {videos.map((video) => hasExpiredFiles(video) ? (
              <ExpiredCard key={video.id} video={video} onDelete={() => setDeleteTarget(video.id)} />
            ) : (
              <VideoCard key={video.id} video={video} onClick={() => setSelected(video)} onDelete={() => setDeleteTarget(video.id)} hasPurchased={hasPurchased} />
            ))}
            {isFetchingNextPage &&
              Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={`skel-${i}`} />
              ))}
          </div>
          <div ref={sentinelRef} className="h-1" />
        </>
      )}

      {videos.length > 0 && (
        <div className="mt-6 text-center">
          <p className="text-[13px] text-muted-foreground">
            Unhappy with a generation? <a href="mailto:support@animepup.com" className="text-[#d4a574] font-semibold hover:underline">Send us an email</a> and we'll help you out :)
          </p>
        </div>
      )}

      {selected && <AssetModal video={selected} onClose={() => setSelected(null)} hasPurchased={hasPurchased} />}
      {deleteTarget && (
        <DeleteConfirmModal
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

/**
 * Zips the whole library server-side. The API hands back a short-lived signed link
 * (a plain link can't carry the session header) that the browser downloads directly.
 */
function DownloadAllButton() {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      const { url } = await api.post<{ success: boolean; url: string }>("/videos/export");
      window.location.assign(url);
      // The server lists the library before the file arrives; hold the spinner so
      // the click isn't repeated.
      setTimeout(() => setStarting(false), 4000);
    } catch (err) {
      setError(err instanceof ApiError ? err.errorMessage : "Could not start the download. Please try again.");
      setStarting(false);
    }
  }, []);

  return (
    <div className="flex items-center gap-3 flex-wrap justify-end">
      {error && <p role="alert" className="text-[12px] text-red-400">{error}</p>}
      <button
        onClick={start}
        disabled={starting}
        className="btn-glass inline-flex items-center gap-2 text-[13px] py-2 px-4 rounded-xl disabled:opacity-60"
      >
        {starting ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
        Download all
      </button>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-white/6 card-glass overflow-hidden">
      <div className="w-full aspect-square bg-white/[0.03] animate-pulse" />
      <div className="px-2.5 py-2 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="h-2 w-14 rounded-full bg-white/[0.06] animate-pulse" />
          <div className="h-2 w-16 rounded bg-white/[0.04] animate-pulse" />
        </div>
      </div>
    </div>
  );
}

/** Same footprint as VideoCard, but the files are deleted: no media requests, preview or download. */
function ExpiredCard({ video, onDelete }: { video: Video; onDelete: () => void }) {
  return (
    <div className="group rounded-2xl border border-white/6 card-glass overflow-hidden">
      <div className="relative w-full aspect-square bg-white/3 flex flex-col items-center justify-center gap-1.5 px-3 text-center">
        <Hourglass className="size-5 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-[12px] font-bold text-foreground/70">Expired</span>
        <span className="text-[10px] text-muted-foreground">Deleted after {RETENTION_DAYS} days</span>
        <button
          onClick={onDelete}
          className="absolute top-2 right-2 p-1.5 rounded-xl bg-black/50 backdrop-blur-sm text-white/80 hover:text-red-400 border border-white/10 transition-all md:opacity-0 md:group-hover:opacity-100"
        >
          <Trash2 className="size-3" />
        </button>
      </div>
      <div className="px-2.5 py-2">
        <div className="flex items-center justify-end">
          <span className="text-[9px] text-muted-foreground tabular-nums py-0.5">{new Date(video.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

function VideoCard({ video, onClick, onDelete, hasPurchased }: { video: Video; onClick: () => void; onDelete: () => void; hasPurchased: boolean }) {
  const { download, downloading } = useDownload();
  const displayUrl = hasPurchased ? video.url : (video.watermarked_url || video.url);
  const downloadUrl = video.download_url;
  const statusConfig: Record<string, { color: string; bg: string }> = {
    completed: { color: "text-emerald-500", bg: "bg-emerald-500/10" },
    processing: { color: "text-amber-500", bg: "bg-amber-500/10" },
    pending: { color: "text-amber-500", bg: "bg-amber-500/10" },
    failed: { color: "text-red-400", bg: "bg-red-400/10" },
  };
  const sc = statusConfig[video.status] || statusConfig.pending;

  return (
    <div
      onClick={video.status === "completed" ? onClick : undefined}
      className={cn(
        "group rounded-2xl border border-white/6 card-glass overflow-hidden hover:border-white/12 transition-all",
        video.status === "completed" && "cursor-pointer card-hover",
      )}
    >
      {video.status === "completed" && video.url ? (
        <div className="relative w-full aspect-square bg-black overflow-hidden">
          {video.media_type === "IMAGE" ? (
            <img src={displayUrl!} alt={video.prompt} className="w-full h-full object-contain" />
          ) : (
            <video src={`${displayUrl!}#t=0.1`} className="w-full h-full object-contain" muted loop playsInline preload="metadata"
              onPointerEnter={(e) => e.currentTarget.play().catch(() => {})}
              onPointerLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
            />
          )}
          <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            {downloadUrl && (
              <button
                onClick={(e) => { e.stopPropagation(); download(downloadUrl, getFilename(video)); }}
                disabled={downloading}
                className="p-1.5 rounded-xl bg-black/50 backdrop-blur-sm text-white/80 hover:text-white border border-white/10 hover:bg-black/60 transition-all"
              >
                {downloading ? <Loader2 className="size-3 animate-spin" /> : <Download className="size-3" />}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 rounded-xl bg-black/50 backdrop-blur-sm text-white/80 hover:text-red-400 border border-white/10 hover:bg-black/60 transition-all"
            >
              <Trash2 className="size-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className="relative w-full aspect-square bg-white/3 flex items-center justify-center">
          {video.status === "failed" ? (
            <span className="text-[10px] text-red-400">Failed</span>
          ) : (
            <Loader2 className="size-5 animate-spin text-[#d4a574]" />
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="absolute top-2 right-2 p-1.5 rounded-xl bg-black/50 backdrop-blur-sm text-white/80 hover:text-red-400 border border-white/10 transition-all md:opacity-0 md:group-hover:opacity-100"
          >
            <Trash2 className="size-3" />
          </button>
        </div>
      )}
      <div className="px-2.5 py-2">
        <div className="flex items-center justify-between">
          <span className={cn("text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full", sc.color, sc.bg)}>
            {video.status}
          </span>
          <span className="text-[9px] text-muted-foreground tabular-nums">{new Date(video.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

function AssetModal({ video, onClose, hasPurchased }: { video: Video; onClose: () => void; hasPurchased: boolean }) {
  const { download, downloading } = useDownload();
  const displayUrl = hasPurchased ? video.url : (video.watermarked_url || video.url);
  const downloadUrl = video.download_url;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} style={{ zIndex: 0 }} />
      <div className="relative w-full max-w-2xl mx-4 rounded-3xl overflow-hidden border border-white/10 bg-[#231c2e] animate-modal-in" style={{ zIndex: 1 }}>
        <button onClick={onClose} className="absolute top-3 right-3 z-10 p-2 rounded-xl bg-black/50 backdrop-blur-sm text-white/70 hover:text-white border border-white/10 transition-colors">
          <X className="size-4" />
        </button>
        {video.url && (
          <div className="w-full bg-black flex items-center justify-center" style={{ height: "50vh" }}>
            {video.media_type === "IMAGE" ? (
              <img src={displayUrl!} alt={video.prompt} className="max-w-full max-h-full object-contain" />
            ) : (
              <video src={displayUrl!} className="max-w-full max-h-full object-contain" controls autoPlay loop playsInline />
            )}
          </div>
        )}
        <div className="p-4">
          {!hasPurchased && (
            <Link
              to="/billing"
              onClick={onClose}
              className="flex items-center gap-2.5 p-3 rounded-xl bg-[#d4a574]/8 border border-[#d4a574]/15 mb-3 hover:bg-[#d4a574]/12 transition-colors"
            >
              <Lock className="size-3.5 text-[#d4a574] shrink-0" />
              <span className="text-[11px] font-semibold text-foreground">Remove Watermark</span>
              <span className="text-[10px] text-muted-foreground ml-auto">Get any gizmo pack →</span>
            </Link>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span>{new Date(video.created_at).toLocaleDateString()}</span>
            </div>
            {downloadUrl && (
              <button
                onClick={() => download(downloadUrl, getFilename(video))}
                disabled={downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/6 border border-white/10 text-[11px] font-semibold text-foreground hover:bg-white/10 transition-colors"
              >
                {downloading ? <Loader2 className="size-3 animate-spin" /> : <Download className="size-3" />}
                {hasPurchased ? `Download ${video.media_type === "IMAGE" ? "Image" : "Video"}` : "Download Preview"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function DeleteConfirmModal({ isPending, onConfirm, onCancel }: { isPending: boolean; onConfirm: () => void; onCancel: () => void }) {
  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} style={{ zIndex: 0 }} />
      <div className="relative w-full max-w-sm mx-4 rounded-3xl border border-white/10 bg-[#231c2e] p-6 animate-modal-in text-center" style={{ zIndex: 1 }}>
        <div className="size-12 rounded-2xl bg-red-400/10 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="size-5 text-red-400" />
        </div>
        <h3 className="text-[15px] font-bold text-foreground mb-1">Delete Creation</h3>
        <p className="text-xs text-muted-foreground mb-5">This action cannot be undone.</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-glass flex-1 py-2.5 text-[13px]">Cancel</button>
          <button onClick={onConfirm} disabled={isPending}
            className="flex-1 py-2.5 rounded-2xl bg-red-400/10 border border-red-400/20 text-red-400 text-[13px] font-bold hover:bg-red-400/15 transition-colors flex items-center justify-center gap-2">
            {isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
